import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger } from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { FileStack, ClipboardList, PlayCircle, CheckCircle2, AlertCircle } from 'lucide-react'
import { cn } from '@/lib/utils'
import { getStatusPillClass } from './RecoveryTrackingHeader'
import { recoverySetsApi, type RequestObjectDetailRow } from '../api'
import { buildIconPath } from '@/lib/imagePaths'

function formatVaultDate(iso: string | null | undefined): string {
    if (!iso) return '—'
    try {
        const d = new Date(iso)
        return d.toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })
    } catch {
        return String(iso)
    }
}

/** Same doughnut as ConflictResolutionModal / UnassignedRequestDetailPage */
function DoughnutChart({ percentage, size = 36 }: { percentage: number; size?: number }) {
    const r = (size - 4) / 2
    const circumference = 2 * Math.PI * r
    const pct = Math.min(100, Math.max(0, percentage))
    const strokeDashoffset = circumference * (1 - pct / 100)
    return (
        <div className="relative flex-shrink-0" style={{ width: size, height: size }} title={`${Math.round(pct)}%`}>
            <svg width={size} height={size} className="rotate-[-90deg]">
                <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="currentColor" strokeWidth={3} className="text-muted-foreground/30" />
                <circle
                    cx={size / 2}
                    cy={size / 2}
                    r={r}
                    fill="none"
                    stroke="currentColor"
                    strokeWidth={3}
                    strokeDasharray={circumference}
                    strokeDashoffset={strokeDashoffset}
                    strokeLinecap="round"
                    className="text-green-600 transition-all duration-300"
                />
            </svg>
            <span className="absolute inset-0 flex items-center justify-center text-[10px] font-medium text-foreground">{Math.round(pct)}%</span>
        </div>
    )
}

function hasAssignmentStatus(s: string | null | undefined): boolean {
    if (s == null) return false
    const t = String(s).trim()
    return t !== '' && !/^(null|none)$/i.test(t)
}

/** Analytics circle: 4/0 = green, 3/1 = amber, 2 = red, null/other = grey. Coerces to number for API string values. */
function getAnalyticsCircle(analytics: number | string | null | undefined) {
    const raw = analytics ?? null
    const v = raw !== null && raw !== undefined && raw !== '' ? Number(raw) : null
    const num = typeof v === 'number' && !Number.isNaN(v) ? v : null
    const color =
        num === null ? 'grey'
        : num === 0 || num === 4 ? 'green'
        : num === 1 || num === 3 ? 'amber'
        : num === 2 ? 'red'
        : 'grey'
    const colorClasses = { green: 'bg-green-500', amber: 'bg-amber-500', red: 'bg-red-500', grey: 'bg-gray-500' }
    const pulseClass = color === 'red' ? 'animate-analytics-pulse-red' : color === 'amber' ? 'animate-analytics-pulse-amber' : ''
    return (
        <div
            className={cn('h-4 w-4 shrink-0 rounded-full', colorClasses[color as keyof typeof colorClasses], pulseClass)}
            title={num !== null ? String(num) : '—'}
        />
    )
}

/** Runbook block matching ConflictResolutionModal / UnassignedRequestDetailPage / RecoveryRunbookProgressSection */
function RunbookBlock({ row, t }: { row: RequestObjectDetailRow; t: (key: string) => string }) {
    const noTasks = row.totalTasks ?? 0
    const noStarted = row.startedTasks ?? 0
    const noCompleted = row.completedTasks ?? 0
    const noIssues = row.issueTasks ?? 0
    const pct = row.percentComplete ?? (noTasks > 0 && noTasks != null ? (noCompleted / noTasks) * 100 : 0)
    if (noTasks === 0 && (row.engineerName == null || (row.engineerName ?? '').trim() === '')) {
        return (
            <div className="flex flex-col gap-1">
                <span className="text-xs text-muted-foreground">{t('recoverRequests.noRunbook')}</span>
            </div>
        )
    }
    return (
        <div className="flex flex-col gap-1.5">
            {row.engineerName != null && (row.engineerName ?? '').trim() !== '' && (
                <div className="flex items-center gap-1.5 flex-wrap">
                    <span className="text-xs text-muted-foreground">{t('recoverRequests.engineerName', 'Engineer')}:</span>
                    <span className="inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium bg-primary/15 text-primary dark:bg-primary/25 dark:text-primary-foreground">
                        {row.engineerName}
                    </span>
                </div>
            )}
            {noTasks === 0 || noTasks == null ? (
                <span className="text-xs text-muted-foreground">{t('recoverRequests.noRunbook')}</span>
            ) : (
                <div className="flex items-center gap-2 flex-nowrap">
                    <DoughnutChart percentage={pct} size={36} />
                    <div className="flex items-center gap-1.5 flex-shrink-0 flex-wrap">
                        <div className="flex items-center gap-1 rounded px-1.5 py-0.5 bg-primary/10">
                            <ClipboardList className="h-3.5 w-3.5 text-primary" />
                            <span className="text-xs font-medium">{noTasks}</span>
                        </div>
                        <div className="flex items-center gap-1 rounded px-1.5 py-0.5 bg-amber-500/10">
                            <PlayCircle className="h-3.5 w-3.5 text-amber-600" />
                            <span className="text-xs font-medium">{noStarted}</span>
                        </div>
                        <div className="flex items-center gap-1 rounded px-1.5 py-0.5 bg-green-500/10">
                            <CheckCircle2 className="h-3.5 w-3.5 text-green-600" />
                            <span className="text-xs font-medium">{noCompleted}</span>
                        </div>
                        <div className="flex items-center gap-1 rounded px-1.5 py-0.5 bg-red-500/10">
                            <AlertCircle className="h-3.5 w-3.5 text-red-600" />
                            <span className="text-xs font-medium">{noIssues}</span>
                        </div>
                    </div>
                </div>
            )}
        </div>
    )
}

export function RequestObjectDetailsPopover({ requestID, requestLabel, setId }: { requestID: string; requestLabel?: string; setId?: number | null }) {
    const { t } = useTranslation()
    const [open, setOpen] = useState(false)
    const { data, isLoading, isError } = useQuery({
        queryKey: ['recovery-set', setId != null ? 'set-request-object-details' : 'request-object-details', setId ?? 0, requestID],
        queryFn: async () => {
            const res = setId != null && setId > 0
                ? await recoverySetsApi.getSetRequestObjectDetails(setId, requestID)
                : await recoverySetsApi.getRequestObjectDetails(requestID)
            const payload = (res.data as { data?: RequestObjectDetailRow[] })?.data
            return payload ?? []
        },
        enabled: open && !!requestID,
    })
    const rows = data ?? []
    const displayLabel = requestLabel ?? requestID

    return (
        <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
                <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8 shrink-0 rounded-md"
                    aria-label={t('recoverRequests.viewObjectDetails', 'View object details')}
                    onClick={(e) => e.stopPropagation()}
                    onPointerDown={(e) => e.stopPropagation()}
                >
                    <FileStack className="h-4 w-4 text-blue-600 hover:text-blue-700" />
                </Button>
            </DialogTrigger>
            <DialogContent className="max-w-[min(95vw,920px)] max-h-[85vh] overflow-hidden flex flex-col p-0 gap-0" aria-describedby="request-object-details-desc">
                <DialogHeader className="px-6 pt-6 pb-2 text-left">
                    <DialogTitle>{t('recoverRequests.objectDetailsTitle', 'Request object details')}</DialogTitle>
                    <DialogDescription id="request-object-details-desc">
                        {t('recoverRequests.requestIdLabel', 'Request ID')}: {displayLabel}
                    </DialogDescription>
                </DialogHeader>
                <div className="rounded-md border mx-6 mb-6 overflow-hidden min-w-0 flex-1 min-h-0 overflow-y-auto">
                    <div className="grid grid-cols-[minmax(280px,2fr)_minmax(48px,auto)_minmax(150px,auto)_minmax(300px,1.5fr)] gap-2 bg-muted/50 px-3 py-1.5 text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                        <div className="text-foreground">{t('recoverRequests.object', 'Object')}</div>
                        <div className="text-foreground">{t('recoverRequests.analytics', 'Analytics')}</div>
                        <div className="text-foreground">{t('recoverRequests.conflictResolution.dateVaultedLabel', 'Date Vaulted')}</div>
                        <div className="text-foreground">{t('recoverRequests.runbook', 'Runbook')}</div>
                    </div>
                    {isLoading && (
                        <div className="p-4 text-sm text-muted-foreground border-t">{t('common.loading', 'Loading…')}</div>
                    )}
                    {isError && (
                        <div className="p-4 text-sm text-destructive border-t">{t('common.error', 'Failed to load.')}</div>
                    )}
                    {!isLoading && !isError && rows.length === 0 && (
                        <div className="p-4 text-sm text-muted-foreground border-t">{t('recoverRequests.noObjectDetails', 'No object details.')}</div>
                    )}
                    {rows.map((row) => {
                        const showAssignment = hasAssignmentStatus(row.assignmentStatus)
                        return (
                            <div
                                key={row.objectID ?? row.recoveryHierarchyId ?? Math.random()}
                                className="grid grid-cols-[minmax(280px,2fr)_minmax(48px,auto)_minmax(150px,auto)_minmax(300px,1.5fr)] gap-2 items-start px-3 py-2 text-xs border-t border-border"
                            >
                                <div className="min-w-0 flex items-center gap-2">
                                    <div className="flex flex-col gap-0.5 min-w-0 flex-1">
                                        <span className="font-semibold text-foreground truncate">{row.objectID ?? '—'}</span>
                                        <span className="text-muted-foreground truncate">{row.objectDescription ?? '—'}</span>
                                    </div>
                                    {row.iconFilename && (
                                        <img src={buildIconPath(row.iconFilename)} alt="" className="h-7 w-7 object-contain shrink-0" />
                                    )}
                                </div>
                                <div className="flex items-center">
                                    {getAnalyticsCircle(row.analytics)}
                                </div>
                                <div className="min-w-0 pt-0.5">
                                    <span className="text-foreground">{row.vaultedDateTime ? formatVaultDate(row.vaultedDateTime) : t('recoverRequests.conflictResolution.managementOnly', 'Management Only')}</span>
                                </div>
                                <div className="min-w-0 flex flex-col gap-1">
                                    {showAssignment && (
                                        <>
                                            <span className={cn('rounded px-1.5 py-0.5 text-[10px] font-medium w-fit', getStatusPillClass(row.assignmentStatus!))}>{row.assignmentStatus}</span>
                                            <RunbookBlock row={row} t={t} />
                                        </>
                                    )}
                                </div>
                            </div>
                        )
                    })}
                </div>
            </DialogContent>
        </Dialog>
    )
}
